import { Texture } from 'pixi.js';
import type { BuildingId } from '@data/buildings';
import type { ResourceId } from '@data/resources';
import { visualAge, visualEraIndex } from '@data/visual-ages';

// Integer raster primitives: every edge is authored on the same 2-screen-pixel grid.
// Textures are baked once; no drawing or allocations in the animation loop.
export const P = { ink:'#302b30', dark:'#4f3a26', wood:'#7a4e2a', timber:'#a5744a', lightWood:'#d1a56e',
  felt:'#f2e8cc', cream:'#fff8e6', tan:'#e6d6ac', shade:'#b89c78', stone:'#9a958c',
  pale:'#dccfb2', rock:'#6c6a66', teal:'#3fa7c4', cyan:'#7fd4dc', blue:'#1f6e86',
  gold:'#e8b34a', red:'#8f2723', brick:'#a5553f', iron:'#4a4f5a', steel:'#a8b0b8',
  green:'#6e9448', leaf:'#a7b96b', forest:'#405d3b', violet:'#7a6fd0' };
type Point = [number, number];
export class Raster {
  canvas = document.createElement('canvas');
  ctx: CanvasRenderingContext2D;
  constructor(public w=48, public h=56) { this.canvas.width=w; this.canvas.height=h; this.ctx=this.canvas.getContext('2d')!; }
  rect(x:number,y:number,w:number,h:number,c:string) { this.ctx.fillStyle=c; this.ctx.fillRect(Math.round(x),Math.round(y),Math.round(w),Math.round(h)); }
  poly(pts:Point[],c:string) {
    const lo=Math.floor(Math.min(...pts.map(p=>p[1]))), hi=Math.ceil(Math.max(...pts.map(p=>p[1])));
    for(let y=lo;y<hi;y++) {
      const xs:number[]=[];
      for(let i=0;i<pts.length;i++) { const a=pts[i],b=pts[(i+1)%pts.length];
        if((a[1]<=y+.5&&b[1]>y+.5)||(b[1]<=y+.5&&a[1]>y+.5)) xs.push(a[0]+(y+.5-a[1])*(b[0]-a[0])/(b[1]-a[1]));
      }
      xs.sort((a,b)=>a-b); for(let i=0;i+1<xs.length;i+=2) this.rect(Math.ceil(xs[i]-.5),y,Math.ceil(xs[i+1]-.5)-Math.ceil(xs[i]-.5),1,c);
    }
  }
  line(x:number,y:number,u:number,v:number,c:string) { const n=Math.max(Math.abs(u-x),Math.abs(v-y)); for(let i=0;i<=n;i++) this.rect(x+(u-x)*i/(n||1),y+(v-y)*i/(n||1),1,1,c); }
  ellipse(x:number,y:number,rx:number,ry:number,c:string) { for(let j=-ry;j<=ry;j++) { const dx=Math.floor(rx*Math.sqrt(Math.max(0,1-j*j/(ry*ry)))); this.rect(x-dx,y+j,dx*2+1,1,c); } }
  texture() { const out=document.createElement('canvas'); out.width=this.w*2;out.height=this.h*2; const ctx=out.getContext('2d')!;ctx.imageSmoothingEnabled=false;ctx.drawImage(this.canvas,0,0,out.width,out.height);const t=Texture.from(out);t.source.scaleMode='nearest';return t; }
}
const cache=new Map<string,Texture>();
function banner(r:Raster,x:number,y:number,h=17) { r.rect(x,y,1,h,P.dark);r.rect(x+1,y+1,7,8,P.blue);r.rect(x+1,y,6,6,P.teal);r.rect(x+3,y+1,1,3,P.cream);r.rect(x+2,y+2,3,1,P.cream);r.rect(x,y-2,1,2,P.gold); }
function door(r:Raster,x:number,y:number) { r.rect(x-1,y-1,7,9,P.wood);r.rect(x,y,5,8,P.ink);r.rect(x+1,y+1,3,5,P.dark);r.rect(x+3,y+4,1,1,P.gold); }
function yurt(r:Raster,x:number,y:number,s:number,royal=false) {
  r.ellipse(x,y+1,s+2,Math.round(s*.37),P.dark);
  r.ellipse(x,y-4,s,Math.round(s*.36),P.shade);
  r.rect(x-s,y-11,s*2,8,P.shade);
  r.rect(x-s+1,y-11,s+4,9,P.tan);
  r.poly([[x-s,y-11],[x-5,y-23],[x+3,y-25],[x+s,y-12],[x+5,y-7]],P.shade);
  r.poly([[x-s,y-11],[x-5,y-23],[x+2,y-24],[x+6,y-10],[x-2,y-8]],P.felt);
  r.poly([[x-s+2,y-12],[x-4,y-23],[x,y-23],[x-7,y-12]],P.cream);
  r.line(x+2,y-23,x+s-2,y-12,P.tan);r.line(x-3,y-22,x-3,y-9,P.tan);
  r.ellipse(x,y-10,s,3,P.tan);
  r.rect(x-s+1,y-9,s*2-2,2,royal?P.blue:P.red);
  for(let i=x-s+3;i<x+s-2;i+=4) {r.rect(i,y-9,1,1,P.gold);r.rect(i+1,y-8,1,1,P.felt);}
  for(let i=x-s+2;i<x+s-1;i+=5) r.rect(i,y-6,1,5,P.shade);
  r.rect(x-4,y-25,7,2,P.dark);r.rect(x-3,y-26,5,1,P.lightWood);
  door(r,x-2,y-6);
  r.poly([[x-3,y+2],[x+3,y+2],[x+8,y+5],[x+1,y+6]],P.red);
  r.line(x-1,y+3,x+5,y+5,P.gold);
}
function hall(r:Raster,x:number,y:number,era:number,wide=false,domed=true,chimney=true) {
  const w=wide?19:13, h=wide?20:15;
  const wall=era===1?P.timber:era===3?P.brick:era===4?P.steel:P.pale;
  const side=era===1?P.wood:era===3?P.red:era===4?P.iron:P.stone;
  r.poly([[x-w,y-h],[x+4,y-h-5],[x+w,y-h],[x+w,y-2],[x+2,y+4],[x-w,y-2]],P.ink);
  r.poly([[x-w+1,y-h],[x+2,y-h+3],[x+2,y+2],[x-w+1,y-3]],wall);
  r.poly([[x+2,y-h+3],[x+w-1,y-h],[x+w-1,y-3],[x+2,y+2]],side);
  if(era===1) {
    r.poly([[x-w-2,y-h],[x-2,y-h-12],[x+w+2,y-h-6],[x+3,y-h+4]],P.dark);
    r.poly([[x-w,y-h],[x-2,y-h-11],[x+3,y-h+2]],P.lightWood);
    r.poly([[x-2,y-h-11],[x+w+1,y-h-6],[x+3,y-h+2]],P.wood);
    for(let j=0;j<4;j++) r.line(x-1+j*3,y-h-9+j,x+4+j*3,y-h+1-j,P.timber);
    r.line(x-3,y-h-12,x+w+2,y-h-7,P.pale);
    for(let j=0;j<3;j++) r.line(x-w+2,y-5-j*4,x+1,y-j*4,P.wood);
  } else {
    r.poly([[x-w-1,y-h],[x-2,y-h-6],[x+w+1,y-h],[x+2,y-h+5]],era===4?P.iron:P.pale);
    r.poly([[x-w+2,y-h],[x-2,y-h-4],[x+w-2,y-h],[x+2,y-h+3]],era===4?P.violet:P.stone);
    if(era===2&&domed) { r.ellipse(x,y-h-4,9,4,P.blue);r.ellipse(x,y-h-7,8,7,P.teal);r.ellipse(x-2,y-h-9,4,4,P.cyan);r.rect(x,y-h-17,1,4,P.gold); }
    if(era===3&&chimney) {r.rect(x+5,y-h-17,4,17,P.red);r.rect(x+5,y-h-17,1,16,P.brick);r.rect(x+4,y-h-18,6,2,P.pale);}
    if(era===4) {r.line(x-w+1,y-h+1,x+2,y-h+4,P.cyan);r.line(x+2,y-h+4,x+w-1,y-h+1,P.teal);r.rect(x+7,y-h-13,1,12,P.steel);r.rect(x+6,y-h-14,3,2,P.cyan);}
  }
  if(era===3) {
    for(let row=0;row<3;row++) for(let col=0;col<3;col++) {
      const xx=x-w+3+col*4+(row%2)*2, yy=y-h+5+row*4;
      if(xx<x+1)r.rect(xx,yy,2,1,P.timber);
    }
  }
  for(let i=0;i<2;i++) {r.rect(x-w+4+i*6,y-h+4,3,4,P.dark);r.rect(x-w+4+i*6,y-h+4,2,3,era===4?P.cyan:P.gold);}
  r.rect(x+7,y-h+3,3,4,P.ink);r.rect(x+7,y-h+3,1,3,era===4?P.teal:P.shade);
  door(r,x-3,y-6);
  r.line(x-w,y-2,x+2,y+4,P.rock);r.line(x+2,y+4,x+w,y-2,P.dark);
}
function log(r:Raster,x:number,y:number) {r.rect(x,y,10,3,P.wood);r.rect(x,y,10,1,P.lightWood);r.rect(x+9,y+1,2,2,P.tan);r.rect(x+10,y+1,1,1,P.dark);}
function rock(r:Raster,x:number,y:number,s=6,ore=false) {r.poly([[x-s,y],[x-s+2,y-6],[x+1,y-9],[x+s,y-4],[x+s,y],[x,y+3]],P.rock);r.poly([[x-s,y],[x-s+2,y-6],[x+1,y-9],[x+2,y-3],[x,y+1]],ore?P.iron:P.pale);r.poly([[x+1,y-9],[x+s,y-4],[x+2,y-3]],P.stone);if(ore){r.line(x-2,y-5,x+1,y-2,P.gold);r.rect(x+2,y-5,2,2,P.lightWood);r.rect(x-3,y-4,1,1,P.cream);}}
function tree(r:Raster,x:number,y:number,s=10) {r.rect(x-1,y-11,3,12,P.dark);r.rect(x-1,y-10,1,10,P.lightWood);r.line(x,y-4,x+4,y-9,P.wood);
  r.ellipse(x+1,y-15,s,8,P.forest);r.ellipse(x-3,y-19,s-2,7,P.green);r.ellipse(x-4,y-22,s-4,4,P.leaf);r.ellipse(x+5,y-15,4,4,P.green);
  for(let i=0;i<7;i++) r.rect(x-s+3+(i*7)%(s*2-3),y-22+(i*3)%12,2,1,i%2?P.leaf:P.forest);
}
export function buildingArt(id:BuildingId|'keep',age:number,tier:number,variant=0):Texture {
  age=visualAge(age); const era=visualEraIndex(age);
  const level=Math.min(2,Math.max(0,Math.floor(tier/2))), key=`atelier:${id}:${age}:${level}:${variant%2}`;
  const old=cache.get(key);if(old)return old;
  const r=new Raster();const x=24,y=45;
  r.ellipse(x,y+2,id==='keep'?22:17,6,P.dark);r.ellipse(x,y+1,id==='keep'?21:16,5,P.stone);
  if(age>=5) {
    futureBuilding(r,id,age,level);
  } else if(id==='house'||id==='keep') {
    if(era===0)yurt(r,x,y,id==='keep'?19:13,id==='keep');else hall(r,x,y,era,id==='keep',age!==2,false);
    if(id==='keep') {banner(r,4,17,28);banner(r,42,21,23);}
    else if(level>0)banner(r,39,26,19);
  } else if(id==='school'&&era<2) {
    for(let i=0;i<3;i++)rock(r,14+i*9,44+(i%2)*2,4);
    r.poly([[18,43],[18,19],[21,13],[28,14],[30,40],[25,46]],P.rock);
    r.poly([[19,41],[19,20],[22,15],[26,15],[27,41],[24,44]],P.pale);
    for(let i=0;i<4;i++) {r.line(22,20+i*5,25,22+i*5,P.blue);r.line(23,19+i*5,23,23+i*5,P.teal);}
    banner(r,35,25,19);
  } else if(id==='quarry') {
    rock(r,16,43,8);rock(r,29,40,7);rock(r,29,48,5);
    r.rect(10,19,3,24,era>=3?P.iron:P.wood);r.rect(10,19,23,3,P.lightWood);r.line(12,30,24,21,P.timber);r.line(30,22,30,33,P.ink);r.rect(27,33,6,4,P.stone);
    if(era===4){r.rect(29,25,2,12,P.cyan);r.rect(27,36,6,1,P.teal);}
  } else if(id==='mine') {
    rock(r,24,42,15,true);r.rect(17,32,13,13,P.dark);r.rect(18,33,11,12,P.ink);
    r.rect(15,29,3,17,era>=3?P.steel:P.wood);r.rect(30,29,3,17,era>=3?P.iron:P.wood);r.rect(15,28,18,3,P.lightWood);
    r.line(20,40,16,50,P.iron);r.line(26,40,29,50,P.iron);for(let i=0;i<3;i++)r.line(18-i,44+i*2,27+i,44+i*2,P.timber);
    r.rect(19,33,2,3,era===4?P.cyan:P.gold);
    if(era>0){r.line(17,28,22,13,P.timber);r.line(31,28,26,13,P.wood);r.rect(20,12,9,3,P.iron);r.rect(24,15,1,11,P.ink);}
  } else if(id==='lumberCamp') {
    if(era<2){r.rect(10,31,2,14,P.wood);r.rect(33,31,2,14,P.dark);r.poly([[7,31],[16,20],[39,26],[34,34]],P.wood);r.poly([[7,31],[16,20],[20,21],[13,32]],P.lightWood);for(let i=0;i<4;i++)r.line(19+i*4,23+i,15+i*4,32,P.timber);}
    else hall(r,24,42,era,false,age!==2);
    log(r,11,44);log(r,12,41);log(r,23,47);r.rect(35,38,3,9,P.dark);r.rect(33,37,6,3,P.steel);
  } else if(id==='barracks'&&era<2) {
    yurt(r,25,39,10,true);
    for(let i=0;i<7;i++){const a=8+i*5,b=42+(i<3?i:6-i);r.rect(a,b-9,3,12,P.wood);r.rect(a,b-9,1,11,P.lightWood);r.poly([[a-1,b-9],[a+1,b-13],[a+4,b-9]],P.timber);}
    r.line(9,43,24,46,P.dark);r.line(24,46,40,43,P.dark);banner(r,39,15,27);
  } else {
    hall(r,x,y,era,false,age!==2,false);
    if(id==='barracks'&&age===2) {
      for(let i=0;i<4;i++)r.rect(11+i*7,24,4,4,P.pale);
      for(let i=0;i<3;i++){r.rect(31+i*3,30,1,15,P.wood);r.poly([[30+i*3,30],[31+i*3,27],[33+i*3,30]],P.steel);}
    } else if(id==='barracks') {
      r.ellipse(32,29,7,3,P.iron);r.rect(30,23,7,6,P.steel);
      r.rect(34,24,10,3,P.iron);r.rect(42,24,2,2,era===4?P.cyan:P.rock);
      r.rect(31,24,3,1,P.pale);
    }
    if(id==='school') {
      banner(r,7,17,27);
      if(era===2){r.rect(36,18,4,24,P.stone);r.rect(36,18,2,23,P.pale);r.rect(34,18,8,2,P.pale);r.poly([[35,17],[38,9],[41,17]],P.teal);r.rect(38,7,1,2,P.gold);}
      if(era===3){r.rect(12,30,18,1,P.pale);r.rect(13,32,15,5,P.dark);for(let i=0;i<4;i++)r.rect(14+i*3,33,2,3,P.gold);}
      if(era===4){r.ellipse(24,22,11,7,P.blue);r.ellipse(22,20,8,5,P.teal);r.ellipse(20,18,4,2,P.cyan);r.line(24,16,24,27,P.steel);r.line(15,22,33,22,P.steel);}
    }
  }
  if(age===2 && (id==='house'||id==='keep')) {
    // Seljuk stone portals and pitched stone roofs precede Ottoman domes.
    r.poly([[10,29],[22,17],[38,25],[27,34]],P.rock);r.poly([[10,29],[22,17],[27,34]],P.pale);
    r.rect(20,34,8,12,P.stone);r.rect(22,37,4,9,P.ink);r.poly([[22,37],[24,34],[26,37]],P.ink);
  }
  if(age===4 && (id==='house'||id==='school'||id==='keep')) {
    // Civic and residential blocks do not inherit factory smokestacks.
    r.rect(13,32,17,1,P.pale); for(let j=0;j<3;j++)r.rect(14+j*5,34,3,4,P.cyan);
  }
  if(level===2&&age<7){r.rect(22,35,4,3,P.gold);r.rect(23,36,2,1,P.dark);}
  if(variant%2&&id==='house'&&age<4){log(r,33,47);r.rect(10,43,3,4,P.brick);}
  const t=r.texture();cache.set(key,t);return t;
}
export function resourceArt(id:ResourceId,age=0):Texture {
  age=visualAge(age);
  const key=`field:${id}:${age>=5?age:0}`;const old=cache.get(key);if(old)return old;const r=new Raster();
  r.poly([[3,45],[24,35],[45,45],[24,55]],P.dark);r.poly([[5,45],[24,37],[43,45],[24,53]],P.shade);
  for(let i=0;i<8;i++){r.rect(7+i*2,45+i,1,1,P.tan);r.rect(26+i*2,51-i,1,1,P.tan);}
  if(age>=5){
    r.ctx.clearRect(0,0,48,56);futureResource(r,id,age);
  } else if(id==='wood'){tree(r,15,44,9);tree(r,32,43,9);tree(r,24,49,10);}
  else if(id==='knowledge'){rock(r,17,47,6);r.poly([[21,47],[20,24],[25,19],[29,25],[28,46]],P.blue);r.poly([[21,25],[25,21],[25,43],[22,46]],P.teal);for(let i=0;i<4;i++)r.rect(23,26+i*4,3,1,P.cyan);r.rect(32,33,2,2,P.cyan);}
  else{rock(r,17,43,9,id==='ore');rock(r,29,42,8,id==='ore');rock(r,25,49,7,id==='ore');rock(r,36,48,4,id==='ore');}
  const t=r.texture();cache.set(key,t);return t;
}

function platform(r:Raster,x=24,y=46,w=19) {
  r.poly([[x-w,y-4],[x,y-12],[x+w,y-4],[x+w,y],[x,y+7],[x-w,y]],P.iron);
  r.poly([[x-w,y-4],[x,y-12],[x+w,y-4],[x,y+3]],P.steel);
  r.line(x-w+2,y-3,x,y+4,P.cyan);r.line(x,y+4,x+w-2,y-3,P.blue);
}
function moduleBlock(r:Raster,x:number,y:number,w:number,h:number) {
  r.poly([[x-w,y-h],[x,y-h-5],[x+w,y-h],[x+w,y],[x,y+5],[x-w,y]],P.iron);
  r.poly([[x-w+1,y-h],[x,y-h+3],[x,y+3],[x-w+1,y-1]],P.steel);
  r.poly([[x,y-h+3],[x+w-1,y-h],[x+w-1,y-1],[x,y+3]],P.rock);
  r.poly([[x-w,y-h],[x,y-h-5],[x+w,y-h],[x,y-h+3]],P.pale);
  for(let i=0;i<3;i++)r.rect(x-w+2+i*3,y-h+4,2,2,P.blue);
  r.line(x+1,y-h+4,x+w-2,y-h+1,P.cyan);
}
function dome(r:Raster,x:number,y:number,w:number,h:number) {
  r.ellipse(x,y,w,4,P.iron);r.ellipse(x,y-4,w-1,h,P.blue);
  r.ellipse(x-2,y-6,w-4,Math.max(2,h-3),P.teal);
  r.ellipse(x-4,y-9,3,2,P.cyan);
  r.line(x,y-h-4,x,y,P.steel);r.line(x-w+2,y-5,x+w-2,y-5,P.steel);
  r.line(x-w+3,y-1,x+w-3,y-1,P.cyan);
}
function solar(r:Raster,x:number,y:number) {
  r.rect(x+4,y+4,1,6,P.iron);
  r.poly([[x,y],[x+10,y-3],[x+14,y+3],[x+4,y+6]],P.blue);
  for(let j=0;j<3;j++)r.line(x+2+j*3,y,x+5+j*3,y+4,P.teal);
  r.line(x+2,y+2,x+12,y,P.cyan);
}
function futureBuilding(r:Raster,id:BuildingId|'keep',age:number,level:number) {
  r.ctx.clearRect(0,0,48,56);
  if(age>=7){lateBuilding(r,id,age,level);return;}
  platform(r);
  if(id==='house') {
    if(age===5){moduleBlock(r,24,42,13,12);r.rect(20,36,5,10,P.iron);r.rect(21,37,3,2,P.cyan);solar(r,4,36);}
    else if(age===6){moduleBlock(r,24,44,13,8);dome(r,24,31,13,10);r.rect(18,29,4,4,P.green);r.rect(28,31,3,3,P.leaf);}
    else if(age===7){moduleBlock(r,24,43,10,25);r.ellipse(24,22,15,3,P.blue);r.line(10,22,38,22,P.cyan);}
    else if(age===8){moduleBlock(r,19,43,8,29);moduleBlock(r,31,44,6,20);r.line(19,11,19,4,P.cyan);}
    else if(age===9){r.ellipse(24,38,17,4,P.violet);moduleBlock(r,24,29,11,15);r.rect(22,36,4,8,P.cyan);}
    else {r.poly([[24,8],[37,26],[24,40],[11,26]],P.blue);r.poly([[24,8],[24,40],[11,26]],P.cyan);if(age===11){r.ellipse(24,25,19,3,P.violet);r.line(6,26,41,26,P.cream);}r.rect(23,43,2,4,P.teal);}
  } else if(id==='keep') {
    if(age===5){
      r.rect(9,11,3,31,P.iron);r.rect(11,14,11,2,P.steel);r.rect(11,24,10,2,P.steel);
      r.poly([[20,39],[20,17],[24,7],[28,17],[28,39]],P.steel);
      r.poly([[20,17],[24,7],[24,39],[20,39]],P.cream);
      r.rect(21,22,6,3,P.teal);r.poly([[20,30],[15,41],[20,39]],P.blue);r.poly([[28,30],[33,41],[28,39]],P.blue);
      r.rect(22,39,4,4,P.iron);solar(r,30,37);
    } else if(age===6){moduleBlock(r,24,44,16,12);dome(r,24,27,16,12);r.rect(23,8,2,6,P.cyan);}
    else if(age===7||age===8){moduleBlock(r,24,44,8,32);r.ellipse(24,23,19,5,P.violet);r.ellipse(24,22,17,3,P.iron);r.line(7,22,41,22,P.cyan);r.rect(23,4,2,8,P.cream);if(age===8){r.ellipse(24,34,17,4,P.teal);r.line(9,34,39,34,P.cyan);}}
    else {r.ellipse(24,27,16,20,P.iron);r.ellipse(24,27,12,16,P.violet);r.ellipse(24,27,9,13,P.blue);r.ellipse(24,27,6,10,age===11?P.cream:P.cyan);r.rect(8,39,5,7,P.steel);r.rect(35,39,5,7,P.steel);if(age>=10){r.line(4,27,44,27,P.cyan);r.rect(23,3,2,5,P.cream);}}
  } else if(id==='lumberCamp') {
    moduleBlock(r,27,43,11,13);dome(r,27,25,10,7);r.rect(23,23,5,5,P.green);solar(r,3,35);
    r.rect(10,44,4,3,P.green);r.rect(16,44,4,3,P.leaf);
    if(age>=7){r.ellipse(27,17,12,2,P.violet);r.rect(26,12,2,5,P.cyan);}
  } else if(id==='quarry') {
    rock(r,25,44,10);r.rect(10,22,4,22,P.iron);r.rect(10,21,24,4,P.steel);
    r.rect(28,25,4,5,P.blue);r.rect(29,30,2,12,P.cyan);r.rect(25,42,10,1,P.teal);
    r.line(13,32,23,25,P.rock);if(age>=8){r.ellipse(30,33,8,2,P.violet);}
  } else if(id==='mine') {
    r.ellipse(24,42,14,6,P.ink);r.ellipse(24,42,10,4,P.blue);
    r.rect(12,19,3,25,P.steel);r.rect(33,19,3,25,P.iron);r.rect(12,18,24,4,P.steel);
    for(let j=0;j<5;j++)r.poly([[20,23+j*4],[29,23+j*4],[25,28+j*4]],j%2?P.iron:P.teal);
    if(age>=7){r.ellipse(24,30,16,3,P.violet);r.line(10,30,38,30,P.cyan);}
  } else if(id==='barracks') {
    moduleBlock(r,24,43,15,10);r.ellipse(24,28,11,5,P.iron);
    r.poly([[17,27],[22,19],[30,23],[29,30]],P.steel);r.rect(28,22,15,4,P.blue);r.rect(40,22,3,3,P.cyan);
    if(age>=7){r.rect(29,28,13,3,P.violet);r.rect(40,28,2,2,P.cyan);}
    if(age>=9){r.ellipse(24,20,15,3,P.teal);}
  } else {
    moduleBlock(r,24,44,13,12);
    if(age<=6){r.rect(24,18,2,15,P.iron);r.poly([[12,14],[33,22],[30,25],[20,24],[14,19]],P.steel);r.line(16,14,31,19,P.cyan);r.line(25,20,31,12,P.iron);r.rect(31,11,2,2,P.teal);}
    else{r.ellipse(24,22,13,13,P.violet);r.ellipse(24,22,9,9,P.blue);r.poly([[24,12],[30,22],[24,31],[18,22]],P.cyan);r.line(9,22,39,22,P.steel);}
  }
  if(level>0){r.rect(9,47,4,1,P.cyan);r.rect(34,46,3,1,P.cyan);}
}
function futureResource(r:Raster,id:ResourceId,age:number) {
  platform(r,24,46,19);
  if(id==='wood') {dome(r,24,33,15,12);r.rect(20,27,2,13,P.timber);r.ellipse(21,27,6,5,P.green);r.ellipse(27,32,5,4,P.leaf);r.line(24,18,24,35,P.steel);}
  else if(id==='knowledge'){r.rect(13,31,22,14,P.iron);r.rect(15,33,18,9,P.blue);for(let i=0;i<4;i++)r.rect(17+i*4,35,2,5,P.cyan);r.poly([[24,13],[30,23],[24,29],[18,23]],age>=7?P.violet:P.teal);}
  else {rock(r,20,43,10,id==='ore');rock(r,30,45,7,id==='ore');r.rect(8,34,3,13,P.iron);r.rect(7,33,5,2,P.cyan);r.rect(37,37,3,10,P.steel);r.line(11,35,18,40,P.teal);}
}

// Late civilizations have separate construction grammars, not recolored modules.
// All geometry remains inside the original tile anchor and pixel density.
function ribbedTower(r:Raster,x:number,y:number,w:number,h:number,light=P.steel,dark=P.iron,glow=P.cyan) {
  r.poly([[x-w,y-h],[x,y-h-4],[x+w,y-h],[x+w,y],[x,y+4],[x-w,y]],dark);
  r.poly([[x-w,y-h],[x,y-h+3],[x,y+3],[x-w,y]],light);
  r.poly([[x-w,y-h],[x,y-h-4],[x+w,y-h],[x,y-h+3]],P.pale);
  for(let j=4;j<h-2;j+=5){r.line(x-w+1,y-h+j,x-1,y-h+j+3,dark);r.line(x+1,y-h+j+3,x+w-1,y-h+j,glow);}
  r.line(x-w,y-h,x-w,y-1,P.cream);r.rect(x+2,y-4,2,4,P.ink);
}
function orbitalRing(r:Raster,x:number,y:number,rx:number,ry:number,c=P.gold) {
  r.ellipse(x,y+2,rx,ry,P.ink);r.ellipse(x,y,rx,ry,c);
  r.ellipse(x,y,rx-3,Math.max(1,ry-2),P.iron);
  r.line(x-rx+3,y-1,x-3,y-ry+1,P.cream);
  for(let i=0;i<8;i++){const a=i*Math.PI/4;r.rect(x+Math.round(Math.cos(a)*(rx-1)),y+Math.round(Math.sin(a)*(ry-1)),1,1,P.cream);}
}
function serviceDeck(r:Raster,color=P.steel) {
  platform(r);r.poly([[7,45],[23,39],[39,45],[24,51]],P.iron);
  r.line(7,45,24,51,color);r.line(24,51,39,45,P.rock);
  r.rect(21,47,6,2,P.ink);r.rect(22,47,4,1,color);
  for(let i=0;i<3;i++){r.rect(9+i*3,45+i,2,1,P.gold);r.rect(30+i*3,48-i,2,1,color);}
}
function crystal(r:Raster,x:number,y:number,w:number,h:number,c=P.violet) {
  r.poly([[x,y-h],[x+w,y-h/2],[x,y],[x-w,y-h/2]],P.ink);
  r.poly([[x,y-h+1],[x,y-2],[x-w+1,y-h/2]],c);
  r.poly([[x,y-h+1],[x+w-1,y-h/2],[x,y-2]],P.blue);
  r.line(x,y-h+2,x-w+2,y-h/2,P.cyan);r.line(x,y-h+4,x,y-4,P.cream);
}
function lateBuilding(r:Raster,id:BuildingId|'keep',age:number,level:number) {
  const role=['house','keep','lumberCamp','quarry','mine','barracks','school'].indexOf(id);
  serviceDeck(r,age>=10?P.gold:P.steel);
  if(age===7){
    // Stellar engineering: radial machines, heat fins and amber collector surfaces.
    if(id==='house'){
      ribbedTower(r,24,44,6,19);orbitalRing(r,24,29,18,7);
      r.rect(10,27,4,5,P.steel);r.rect(34,28,4,5,P.steel);
      for(let i=0;i<5;i++)r.rect(15+i*4,34,2,1,P.cyan);
    }else if(id==='keep'){
      ribbedTower(r,24,44,5,34);orbitalRing(r,24,20,19,6);orbitalRing(r,24,35,15,5);
      r.ellipse(24,19,6,11,P.gold);r.ellipse(24,19,3,8,P.iron);r.rect(23,6,2,7,P.cream);
      r.line(9,37,15,23,P.steel);r.line(39,37,33,23,P.rock);
    }else if(id==='lumberCamp'){
      ribbedTower(r,24,44,5,17);orbitalRing(r,24,25,9,4);
      for(const [x,y] of [[5,22],[29,17],[31,34],[6,38]]){r.poly([[x,y],[x+10,y-3],[x+13,y+4],[x+3,y+7]],P.lightWood);for(let j=0;j<3;j++)r.line(x+2+j*3,y,x+5+j*3,y+4,P.gold);r.line(x+6,y+3,24,32,P.iron);}
    }else if(id==='quarry'){
      ribbedTower(r,12,43,4,22);ribbedTower(r,36,43,4,22);
      r.line(12,22,24,17,P.steel);r.line(36,22,24,17,P.steel);
      orbitalRing(r,24,35,11,6);rock(r,24,33,6);r.rect(23,20,2,9,P.gold);
    }else if(id==='mine'){
      r.ellipse(24,42,15,6,P.ink);ribbedTower(r,24,40,9,26,P.lightWood,P.iron,P.gold);
      orbitalRing(r,24,19,12,4);for(let i=0;i<3;i++)r.rect(19+i*4,13,2,5,P.steel);
      r.line(11,38,11,26,P.steel);r.line(37,38,37,26,P.steel);
    }else if(id==='barracks'){
      moduleBlock(r,24,42,15,10);orbitalRing(r,24,29,11,4);
      for(const y of [20,27]){r.poly([[12,y],[35,y-5],[41,y-2],[19,y+4]],P.iron);r.line(14,y,36,y-4,P.steel);r.rect(36,y-4,5,2,P.gold);}
    }else{
      ribbedTower(r,24,44,5,23);orbitalRing(r,24,24,17,6);
      r.ellipse(24,20,8,13,P.gold);r.ellipse(24,20,5,10,P.iron);crystal(r,24,26,4,13,P.gold);
      r.line(10,15,38,30,P.steel);
    }
  }else if(age===8){
    // Galactic cities: asymmetric stacks, docking gantries and inhabited terraces.
    if(id==='house'||id==='keep'){
      ribbedTower(r,17,41,7,id==='keep'?32:25);
      ribbedTower(r,30,44,8,id==='keep'?39:32,P.steel,P.blue);
      moduleBlock(r,14,34,9,5);r.line(21,25,37,30,P.cyan);
      r.rect(9,38,6,3,P.green);r.rect(29,9,2,5,P.cyan);
      if(id==='keep'){r.line(6,15,39,20,P.steel);r.rect(4,12,4,8,P.blue);r.rect(38,17,5,5,P.teal);}
    }else if(id==='lumberCamp'){
      for(let i=0;i<3;i++){moduleBlock(r,24,43-i*9,14,6);r.line(12,40-i*9,23,44-i*9,P.green);r.rect(15,38-i*9,3,2,P.leaf);}
      r.rect(8,17,2,26,P.steel);r.rect(38,18,2,25,P.iron);
    }else if(id==='quarry'){
      ribbedTower(r,11,42,4,27);r.rect(10,14,20,4,P.steel);r.line(13,25,25,17,P.rock);
      r.rect(28,17,4,10,P.iron);r.line(30,25,37,33,P.steel);r.line(30,25,26,34,P.steel);rock(r,30,42,10);
    }else if(id==='mine'){
      ribbedTower(r,14,42,5,24);ribbedTower(r,34,43,5,16);
      r.rect(13,17,23,4,P.steel);r.rect(23,21,3,19,P.iron);
      for(let i=0;i<5;i++)r.line(21,24+i*3,28,22+i*3,P.cyan);r.ellipse(25,44,9,3,P.ink);
    }else if(id==='barracks'){
      moduleBlock(r,24,42,18,14);r.rect(15,29,18,15,P.ink);r.rect(16,30,16,2,P.teal);
      r.poly([[13,44],[24,32],[35,44],[24,41]],P.steel);r.rect(23,36,3,3,P.cyan);
      r.rect(6,18,4,14,P.iron);r.rect(5,17,6,3,P.cyan);
    }else{
      ribbedTower(r,24,43,7,24);r.rect(23,7,2,13,P.steel);
      for(const [x,y] of [[8,16],[28,12],[16,28]]){r.poly([[x,y],[x+14,y+5],[x+11,y+9],[x+4,y+8]],P.steel);r.line(x+4,y+2,x+12,y+5,P.teal);r.line(x+8,y+6,x+12,y-1,P.iron);}
    }
  }else if(age===9){
    // Cosmic construction: separated masses suspended over gravity wells.
    r.ellipse(24,43,16,5,P.ink);orbitalRing(r,24,43,15,4,P.violet);
    if(id==='house'){
      moduleBlock(r,16,27,9,13);moduleBlock(r,32,33,8,13);
      r.rect(14,32,3,7,P.violet);r.rect(30,38,3,4,P.teal);r.line(20,23,28,27,P.cyan);
    }else if(id==='keep'||id==='school'){
      r.ellipse(24,23,id==='keep'?16:12,19,P.ink);r.ellipse(24,23,id==='keep'?13:9,16,P.violet);
      r.ellipse(24,23,id==='keep'?9:6,12,P.ink);crystal(r,24,30,5,16);
      for(const [x,y] of [[7,13],[38,19],[10,36]])crystal(r,x,y+5,3,9);
    }else if(id==='lumberCamp'){
      for(const [x,y] of [[12,32],[24,26],[36,33]]){r.ellipse(x,y,6,9,P.iron);r.ellipse(x,y-1,4,7,P.violet);r.rect(x-1,y-3,3,5,P.green);r.rect(x-2,y+9,4,2,P.cyan);}
    }else if(id==='quarry'){
      for(const [x,y] of [[14,27],[29,22],[31,37]])rock(r,x,y,5);
      r.line(8,38,12,13,P.violet);r.line(39,39,36,13,P.violet);r.line(12,13,36,13,P.cyan);
    }else if(id==='mine'){
      orbitalRing(r,24,26,19,7,P.violet);r.ellipse(24,26,9,6,P.ink);
      r.line(7,30,14,40,P.steel);r.line(41,30,34,40,P.steel);r.rect(23,12,2,8,P.cyan);
    }else{
      for(const [x,y] of [[11,30],[24,19],[37,30]]){crystal(r,x,y+5,5,12);r.rect(x-1,y+4,3,5,P.iron);}
      r.line(11,30,24,35,P.violet);r.line(37,30,24,35,P.violet);r.rect(23,32,3,8,P.cyan);
    }
  }else if(age===10){
    // Sky civilization: white cantilevers and swept wings, no dark box skyline.
    const y=id==='keep'?27:33,w=id==='keep'?21:17;
    r.poly([[24-w,y-5],[24-w+4,y+6],[24,y+12],[24+w-4,y+6],[24+w,y-5],[24,y+4]],P.steel);
    r.poly([[24-w,y-5],[24-w+6,y-1],[24,y+6],[24+w-6,y-1],[24+w,y-5],[24,y+2]],P.cream);
    r.line(24-w+3,y+4,24,y+11,P.gold);r.line(24,y+11,24+w-3,y+4,P.teal);
    if(id==='house'){dome(r,24,29,8,8);r.rect(23,39,2,6,P.gold);}
    else if(id==='keep'){crystal(r,24,32,7,28,P.cyan);r.line(5,18,24,33,P.gold);r.line(43,18,24,33,P.gold);}
    else if(id==='lumberCamp'){for(const x of [13,24,35]){r.ellipse(x,27,5,7,P.cream);r.ellipse(x,27,3,5,P.green);}r.line(10,37,38,37,P.gold);}
    else if(id==='quarry'){r.line(9,20,24,36,P.cream);r.line(39,20,24,36,P.cream);rock(r,24,29,6);r.rect(23,17,2,7,P.cyan);}
    else if(id==='mine'){r.ellipse(24,29,8,12,P.pale);r.ellipse(24,29,5,9,P.blue);r.rect(23,20,2,18,P.cyan);}
    else if(id==='barracks'){for(const x of [12,24,36]){r.poly([[x-3,29],[x,13],[x+3,29]],P.cream);r.rect(x,15,1,12,P.gold);}}
    else{orbitalRing(r,24,23,13,5,P.cream);r.ellipse(24,21,5,10,P.gold);r.ellipse(24,21,3,7,P.blue);}
  }else{
    // Eternal civilization: interlocked gold arches and luminous nested chambers.
    r.poly([[5,42],[24,34],[43,42],[24,50]],P.lightWood);
    r.poly([[10,41],[24,35],[38,41],[24,46]],P.cream);
    const rx=id==='keep'?16:12,ry=id==='keep'?20:15,cy=id==='keep'?23:28;
    r.ellipse(24,cy,rx,ry,P.gold);r.ellipse(24,cy,rx-3,ry-3,P.ink);
    if(id==='house'){r.ellipse(24,cy,7,10,P.pale);r.ellipse(24,cy,4,7,P.teal);r.line(12,28,36,28,P.cream);}
    else if(id==='keep'){r.ellipse(24,cy,9,16,P.cream);r.ellipse(24,cy,6,13,P.blue);orbitalRing(r,24,23,21,5,P.gold);r.rect(23,9,2,28,P.cream);}
    else if(id==='lumberCamp'){for(const x of [19,29]){r.ellipse(x,28,4,8,P.green);r.line(x,23,x,33,P.leaf);}r.line(13,39,35,39,P.gold);}
    else if(id==='quarry'){crystal(r,24,35,7,17,P.pale);r.line(9,21,17,26,P.cream);r.line(39,21,31,26,P.cream);}
    else if(id==='mine'){orbitalRing(r,24,30,17,5,P.cream);r.ellipse(24,27,5,8,P.ink);r.rect(23,19,2,8,P.cyan);}
    else if(id==='barracks'){r.poly([[10,39],[24,13],[38,39],[24,32]],P.pale);r.line(24,16,24,33,P.gold);r.rect(23,12,2,6,P.cream);}
    else{r.ellipse(24,26,7,10,P.cream);r.ellipse(24,26,4,7,P.violet);r.line(12,18,36,36,P.gold);r.line(12,36,36,18,P.gold);}
    for(let i=0;i<3;i++)r.rect(19+i*4,45,2,1,P.gold);
  }
  // Consistent maintenance detail: edge bolts, hatches and a tier-dependent status rail.
  for(const x of [8,39]){r.rect(x,46,1,1,P.cream);r.rect(x+1,47,1,1,P.ink);}
  for(let i=0;i<=level;i++)r.rect(20+i*3,49,2,1,age>=10?P.gold:P.cyan);
  if(role>1){r.rect(8,48,4,1,P.iron);r.rect(9,48,1,1,P.gold);}
}
